/**
 * iter168 — Onglet Relances (Facturation).
 * Liste des factures échues non payées, hors poursuite/payée.
 * - Colonnes : n°, client, jours de retard, montant, niveau, action.
 * - Filtres : niveau (0/1/2/3), échelonnement uniquement, tri jours desc.
 * - Actions par ligne :
 *     • Générer rappel N (open dialog confirm)
 *     • Voir/télécharger PDF du dernier rappel
 *     • Marquer en poursuite
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertTriangle, Download, Loader2, Mail, Gavel, Search,
  Layers, Send, XCircle, EyeOff, Shield, Timer, CheckCheck,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const NIVEAU_LABELS = {
  0: "Jamais relancée",
  1: "1er rappel",
  2: "2ème rappel",
  3: "3ème rappel",
};
const NIVEAU_COLORS = {
  0: "bg-gray-50 text-gray-600 border-gray-200",
  1: "bg-yellow-50 text-yellow-700 border-yellow-200",
  2: "bg-orange-50 text-orange-700 border-orange-200",
  3: "bg-red-50 text-red-700 border-red-200",
};
const FRAIS_MAP = { 1: 0, 2: 15, 3: 30 };

const fmt = (n) => Number(n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function RelancesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [niveauFilter, setNiveauFilter] = useState("all");
  const [installmentsOnly, setInstallmentsOnly] = useState(false);
  const [showPartials, setShowPartials] = useState(true);  // iter171 — toggle affichage paiements partiels
  const [assuranceOnly, setAssuranceOnly] = useState(false);  // iter173 — filtre Assurance
  const [refreshTick, setRefreshTick] = useState(0);
  const [dialog, setDialog] = useState(null);  // {type:'relance'|'poursuite', facture, niveau}
  const [mailsFacture, setMailsFacture] = useState(null);  // iter276 — mails envoyés
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/factures/relances/candidates");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData, refreshTick]);

  const filtered = items.filter((it) => {
    if (q) {
      const qq = q.toLowerCase();
      if (
        !(it.numero || "").toLowerCase().includes(qq)
        && !(it.contact_label || "").toLowerCase().includes(qq)
      ) return false;
    }
    if (niveauFilter !== "all" && String(it.niveau_actuel) !== niveauFilter) return false;
    if (installmentsOnly && !it.has_installments) return false;
    // iter171 — Masquer les paiements partiels si toggle décoché
    if (!showPartials && it.is_partiel) return false;
    // iter173 — Filtre Assurance
    if (assuranceOnly && it.type_client_tarif !== "assurance") return false;
    return true;
  });

  const totalReste = filtered.reduce((s, it) => s + Number(it.reste_a_payer || 0), 0);
  const nbAlertesInstallments = filtered.filter((it) => it.installment_alert).length;
  const nbPartiels = filtered.filter((it) => it.is_partiel).length;
  const nbAssurances = items.filter((it) => it.type_client_tarif === "assurance").length;  // iter173

  const openPdf = async (facture, niveau) => {
    try {
      const { data: blob } = await api.get(`/factures/${facture.id}/relance/pdf?niveau=${niveau}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(`PDF indisponible : ${e.response?.data?.detail || e.message}`);
    }
  };

  const generateRelance = async (facture, niveau, breakInstallments, opts = {}) => {
    try {
      const { data } = await api.post(`/factures/${facture.id}/relance/generer`, {
        niveau,
        break_installments: !!breakInstallments,
        force: !!opts.force,  // iter215 — bypass cooldown 10 jours si case cochée
      });
      toast.success(`${data.label} enregistré (${fmt(data.montant_reclame)} CHF)`);
      // Après création : soit ouvrir PDF (courrier), soit envoyer par mail
      if (opts.mode === "email" && opts.email) {
        await sendEmail(facture, niveau, opts.email, !!opts.saveEmail, !!opts.force, opts);
      } else {
        await openPdf(facture, niveau);
      }
      setDialog(null);
      setRefreshTick((n) => n + 1);
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  const sendEmail = async (facture, niveau, email, saveOnClient, force, opts = {}) => {
    try {
      const { data } = await api.post(`/factures/${facture.id}/relance/email`, {
        niveau,
        email,
        save_email_on_client: !!saveOnClient,
        force: !!force,  // iter215 — bypass cooldown 10 jours
        subject_override: opts.subject || null,   // iter276 — texte relu par la compta
        body_override: opts.body || null,
      });
      toast.success(`Email envoyé à ${data.email}${data.email_saved_on_client ? " (adresse enregistrée sur le client)" : ""}`);
      setRefreshTick((n) => n + 1);
    } catch (e) {
      toast.error(`Envoi email échoué : ${e.response?.data?.detail || e.message}`);
      throw e;
    }
  };

  const markPoursuite = async (facture, note) => {
    try {
      await api.post(`/factures/${facture.id}/poursuite`, { note });
      toast.success(`Facture ${facture.numero} passée en poursuite`);
      setDialog(null);
      setRefreshTick((n) => n + 1);
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  const cancelFacture = async (facture, motif) => {
    try {
      await api.post(`/factures/${facture.id}/annuler`, motif ? { motif } : {});
      toast.success(`Facture ${facture.numero} annulée`);
      setDialog(null);
      setRefreshTick((n) => n + 1);
    } catch (e) {
      toast.error(`Annulation échouée : ${e.response?.data?.detail || e.message}`);
    }
  };

  const hideFromRelances = async (facture) => {
    try {
      await api.post(`/factures/${facture.id}/masquer-relances`);
      toast.success(`Facture ${facture.numero} masquée des rappels`);
      setDialog(null);
      setRefreshTick((n) => n + 1);
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div data-testid="relances-tab">
      {/* En-tête + KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-3 border-gray-200">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Factures à relancer</div>
          <div className="text-2xl font-bold text-gray-900 mt-1" data-testid="kpi-count-relances">{filtered.length}</div>
          <div className="text-xs text-gray-500">sur les {items.length} en retard</div>
        </Card>
        <Card className="p-3 border-gray-200">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Montant total à recouvrer</div>
          <div className="text-2xl font-bold text-red-600 mt-1" data-testid="kpi-montant-relances">{fmt(totalReste)} <span className="text-sm text-gray-500">CHF</span></div>
        </Card>
        <Card className="p-3 border-indigo-200 bg-indigo-50/30">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Paiements partiels</div>
          <div className="text-2xl font-bold text-indigo-700 mt-1" data-testid="kpi-partiels">{nbPartiels}</div>
          <div className="text-xs text-gray-500">factures déjà entamées</div>
        </Card>
        <Card className={`p-3 border ${nbAlertesInstallments > 0 ? "border-orange-300 bg-orange-50/40" : "border-gray-200"}`}>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1">
            <Layers className="w-3 h-3" /> Échelonnements à briser
          </div>
          <div className="text-2xl font-bold text-orange-700 mt-1" data-testid="kpi-installments-alert">{nbAlertesInstallments}</div>
          <div className="text-xs text-gray-500">tranches &gt; 15 jours de retard</div>
        </Card>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="N° facture ou client..." className="pl-10" data-testid="relances-search" />
        </div>
        <Select value={niveauFilter} onValueChange={setNiveauFilter}>
          <SelectTrigger className="w-44" data-testid="relances-niveau-filter">
            <SelectValue placeholder="Niveau" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous niveaux</SelectItem>
            <SelectItem value="0">Jamais relancée</SelectItem>
            <SelectItem value="1">1er rappel</SelectItem>
            <SelectItem value="2">2ème rappel</SelectItem>
            <SelectItem value="3">3ème rappel</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant={installmentsOnly ? "default" : "outline"}
          onClick={() => setInstallmentsOnly((v) => !v)}
          className={installmentsOnly ? "bg-orange-600 hover:bg-orange-700" : ""}
          data-testid="relances-installments-toggle"
        >
          <Layers className="w-4 h-4 mr-2" /> Échelonnées uniquement
        </Button>
        <Button
          type="button"
          variant={showPartials ? "default" : "outline"}
          onClick={() => setShowPartials((v) => !v)}
          className={showPartials ? "bg-indigo-600 hover:bg-indigo-700" : ""}
          data-testid="relances-partials-toggle"
          title={showPartials ? "Cliquer pour masquer les paiements partiels" : "Cliquer pour afficher les paiements partiels"}
        >
          {showPartials ? "Partiels affichés" : "Partiels masqués"}
        </Button>
        {/* iter173 — Toggle Assurance uniquement */}
        <Button
          type="button"
          variant={assuranceOnly ? "default" : "outline"}
          onClick={() => setAssuranceOnly((v) => !v)}
          className={assuranceOnly ? "bg-amber-600 hover:bg-amber-700" : "border-amber-300 text-amber-800 hover:bg-amber-50"}
          data-testid="relances-assurance-toggle"
          title={assuranceOnly ? "Voir toutes les relances" : "Voir uniquement les factures assurance"}
        >
          <Shield className="w-4 h-4 mr-2" />
          {assuranceOnly ? "Assurances uniquement" : `Assurances (${nbAssurances})`}
        </Button>
        <Button variant="outline" onClick={() => setRefreshTick((n) => n + 1)} data-testid="relances-refresh">
          Actualiser
        </Button>
      </div>

      {/* Table */}
      <Card className="border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
              <tr>
                <th className="px-3 py-2 text-left">N° facture</th>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-right">Échéance</th>
                <th className="px-3 py-2 text-right">Retard</th>
                <th className="px-3 py-2 text-right">Reste à payer</th>
                <th className="px-3 py-2 text-right">Dernier encaissement</th>
                <th className="px-3 py-2">Niveau</th>
                <th className="px-3 py-2">État</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-10 text-gray-400">Aucune facture à relancer 🎉</td></tr>
              )}
              {!loading && filtered.map((it) => {
                const niv = it.niveau_actuel || 0;
                const nivSuggere = it.niveau_suggere;
                const canRelance = niv < 3;
                const isInstallmentAlert = it.installment_alert;
                // iter215 — Cooldown 10 jours entre rappels
                const daysUntilNext = it.days_until_next_rappel || 0;
                const rappelReady = it.rappel_ready !== false;  // défaut: true si non fourni
                return (
                <tr key={it.id} className="hover:bg-gray-50" data-testid={`relance-row-${it.id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => navigate(`/facturation/${it.id}`)} className="text-[#3d8a5c] hover:underline font-semibold">
                      {it.numero}
                    </button>
                    {it.type_client_tarif === "assurance" && (
                      <Badge
                        variant="outline"
                        className="ml-1.5 bg-amber-100 text-amber-800 border-amber-300 text-[9px] py-0 px-1 font-semibold"
                        title="Facture destinée à une assurance"
                        data-testid={`relance-assurance-badge-${it.id}`}
                      >
                        <Shield className="w-2.5 h-2.5 mr-0.5 inline" />
                        Assurance
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{it.contact_label || "—"}</td>
                  <td className="px-3 py-2 text-right text-xs text-gray-600 font-mono">{it.date_echeance || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <Badge variant="outline" className={`${it.jours_retard >= 60 ? "bg-red-50 text-red-700 border-red-200" : it.jours_retard >= 30 ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-yellow-50 text-yellow-700 border-yellow-200"}`}>
                      {it.jours_retard} j
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="font-mono font-semibold text-red-600">{fmt(it.reste_a_payer)}</div>
                    {it.is_partiel && (
                      <div className="text-[10px] text-gray-500">
                        sur {fmt(it.montant_brut)} — payé {fmt(it.total_encaisse)}
                      </div>
                    )}
                  </td>
                  {/* iter193 — Colonne Dernier encaissement */}
                  <td className="px-3 py-2 text-right" data-testid={`relance-last-payment-${it.id}`}>
                    {it.last_payment ? (
                      <div>
                        <div className="font-mono text-emerald-700 font-semibold">+{fmt(it.last_payment.montant)}</div>
                        <div className="text-[10px] text-gray-500 font-mono">
                          {it.last_payment.date || "—"}
                          {it.last_payment.mode ? ` · ${it.last_payment.mode}` : ""}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Aucun</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={NIVEAU_COLORS[niv]}>
                      {NIVEAU_LABELS[niv]}
                    </Badge>
                    {it.frais_cumules > 0 && (
                      <div className="text-[10px] text-gray-500 mt-0.5">+ {fmt(it.frais_cumules)} CHF frais</div>
                    )}
                    {/* iter215 — Compte à rebours 10 jours entre 2 rappels */}
                    {niv > 0 && !rappelReady && daysUntilNext > 0 && (
                      <div
                        className="flex items-center gap-1 mt-1 text-[10px] font-medium text-gray-600 bg-gray-100 rounded px-1.5 py-0.5 w-fit"
                        title={`Un rappel a été envoyé il y a peu. Prochain rappel possible dans ${daysUntilNext} jour${daysUntilNext > 1 ? "s" : ""}${it.next_rappel_available_at ? " (le " + it.next_rappel_available_at.slice(0, 10) + ")" : ""}.`}
                        data-testid={`relance-cooldown-${it.id}`}
                      >
                        <Timer className="w-3 h-3" />
                        {daysUntilNext}j
                      </div>
                    )}
                    {niv > 0 && rappelReady && it.date_derniere_relance && (
                      <div
                        className="flex items-center gap-1 mt-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5 w-fit"
                        title="Le délai de 10 jours est écoulé — tu peux envoyer le prochain rappel."
                        data-testid={`relance-ready-${it.id}`}
                      >
                        <CheckCheck className="w-3 h-3" />
                        Prêt
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 space-y-0.5">
                    {it.is_partiel && (
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] w-fit" data-testid={`partiel-badge-${it.id}`}>
                        Partiel
                      </Badge>
                    )}
                    {isInstallmentAlert && (
                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 flex items-center gap-1 w-fit text-[10px]">
                        <AlertTriangle className="w-3 h-3" /> Casser tranches
                      </Badge>
                    )}
                    {it.has_installments && !isInstallmentAlert && !it.installment_broken && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
                        <Layers className="w-3 h-3 inline mr-1" />Échelonnée
                      </Badge>
                    )}
                    {it.installment_broken && (
                      <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-300 text-[10px]">
                        Ech. brisé
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                    {niv > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => openPdf(it, niv)} title="Voir dernier PDF de rappel" data-testid={`relance-pdf-${it.id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {/* iter276 — Relire les mails de rappel réellement envoyés */}
                    {niv > 0 && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => setMailsFacture(it)}
                        title="Voir les mails de rappel envoyés"
                        data-testid={`relance-mails-${it.id}`}
                      >
                        <Mail className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {canRelance && (
                      <Button
                        size="sm"
                        className={
                          !rappelReady
                            ? "bg-gray-200 text-gray-500 hover:bg-gray-300 cursor-not-allowed"
                            : nivSuggere === niv + 1
                              ? "bg-orange-600 hover:bg-orange-700 text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }
                        onClick={() => setDialog({ type: "relance", facture: it, niveau: niv + 1 })}
                        title={
                          !rappelReady
                            ? `Un rappel a été envoyé il y a peu — attendre ${daysUntilNext} jour${daysUntilNext > 1 ? "s" : ""} (ou forcer depuis le dialogue).`
                            : niv === 0 ? "Envoyer le 1er rappel" : `Envoyer le rappel N${niv + 1}`
                        }
                        data-testid={`relance-btn-${it.id}`}
                      >
                        {!rappelReady && <Timer className="w-3 h-3 mr-1" />}
                        {niv === 0 ? "1er rappel" : `Rappel N${niv + 1}`}
                        {FRAIS_MAP[niv + 1] > 0 && <span className="ml-1 text-[10px]">+{FRAIS_MAP[niv + 1]} CHF</span>}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => setDialog({ type: "poursuite", facture: it })}
                      title="Passer en poursuite"
                      data-testid={`poursuite-btn-${it.id}`}
                    >
                      <Gavel className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-600 hover:bg-gray-100"
                      onClick={() => setDialog({ type: "hide", facture: it })}
                      title="Masquer des rappels (facture réglée hors système, arrangement à part)"
                      data-testid={`hide-btn-${it.id}`}
                    >
                      <EyeOff className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-700 hover:bg-red-50"
                      onClick={() => setDialog({ type: "cancel", facture: it })}
                      title="Annuler la facture (extourne CO 957)"
                      data-testid={`cancel-btn-${it.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Dialogs de confirmation */}
      {mailsFacture && (
        <RelanceMailsDialog facture={mailsFacture} onClose={() => setMailsFacture(null)} />
      )}

      {dialog?.type === "relance" && (
        <RelanceConfirmDialog
          facture={dialog.facture}
          niveau={dialog.niveau}
          onClose={() => setDialog(null)}
          onConfirm={(breakIt, opts) => generateRelance(dialog.facture, dialog.niveau, breakIt, opts)}
        />
      )}
      {dialog?.type === "poursuite" && (
        <PoursuiteConfirmDialog
          facture={dialog.facture}
          onClose={() => setDialog(null)}
          onConfirm={(note) => markPoursuite(dialog.facture, note)}
        />
      )}
      {dialog?.type === "cancel" && (
        <CancelFactureDialog
          facture={dialog.facture}
          onClose={() => setDialog(null)}
          onConfirm={(motif) => cancelFacture(dialog.facture, motif)}
        />
      )}
      {dialog?.type === "hide" && (
        <HideRelanceDialog
          facture={dialog.facture}
          onClose={() => setDialog(null)}
          onConfirm={() => hideFromRelances(dialog.facture)}
        />
      )}
    </div>
  );
}

/**
 * iter276 — Mails de rappel réellement envoyés : objet, destinataire, texte et
 * pièces jointes, relus depuis la trace d'envoi.
 */
function RelanceMailsDialog({ facture, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ouvert, setOuvert] = useState(null);

  useEffect(() => {
    api.get(`/factures/${facture.id}/relance/emails`)
      .then(({ data }) => {
        setItems(data.items || []);
        setOuvert((data.items || [])[0]?.id || null);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [facture.id]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="relance-mails-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" /> Mails de rappel — {facture.numero}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
        ) : !items.length ? (
          <div className="py-8 text-center text-sm text-gray-500" data-testid="relance-mails-empty">
            Aucun rappel envoyé par email pour cette facture (les rappels par courrier n&apos;ont pas de texte).
          </div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {items.map((m) => (
              <div key={m.id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`relance-mail-${m.id}`}>
                <button
                  type="button"
                  onClick={() => setOuvert(ouvert === m.id ? null : m.id)}
                  className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="text-sm font-semibold text-gray-900">{m.subject}</div>
                  <div className="text-[11px] text-gray-500">
                    {m.date ? new Date(m.date).toLocaleString("fr-CH") : ""} · à {m.to}
                    {m.niveau ? ` · niveau ${m.niveau}` : ""}
                  </div>
                </button>
                {ouvert === m.id && (
                  <div className="px-3 py-2">
                    <div
                      className="prose prose-sm max-w-none text-sm text-gray-800 [&_p]:my-1"
                      dangerouslySetInnerHTML={{ __html: m.body_html || "" }}
                      data-testid={`relance-mail-body-${m.id}`}
                    />
                    {!!(m.attachments || []).length && (
                      <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500">
                        Pièces jointes : {m.attachments.map((a) => a.filename).join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="relance-mails-close">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RelanceConfirmDialog({ facture, niveau, onClose, onConfirm }) {
  const frais = FRAIS_MAP[niveau] || 0;
  const total = Number(facture.reste_a_payer || 0) + frais;
  const label = { 1: "1er rappel", 2: "2ème rappel", 3: "3ème rappel (dernier avant poursuite)" }[niveau];
  const shouldBreak = facture.installment_alert;
  const [breakInstallments, setBreakInstallments] = useState(shouldBreak);
  // iter169 — Choix du canal : courrier (PDF) ou email
  const [channel, setChannel] = useState("courrier"); // 'courrier' | 'email'
  const [clientEmail, setClientEmail] = useState("");
  const [clientEmails, setClientEmails] = useState([]);  // iter237 — société avec 2 adresses
  const [emailInput, setEmailInput] = useState("");
  const [saveEmail, setSaveEmail] = useState(true);
  const [loadingClient, setLoadingClient] = useState(false);
  // iter215 — Cooldown 10 jours entre rappels
  const rappelReady = facture.rappel_ready !== false;
  const daysUntilNext = facture.days_until_next_rappel || 0;
  const [forceCooldown, setForceCooldown] = useState(false);
  // iter276 — Objet et texte du mail, relus et corrigeables avant l'envoi
  const niveauLabel = { 1: "1er rappel", 2: "2e rappel", 3: "3e rappel / mise en demeure" }[niveau] || "rappel";
  const [subject, setSubject] = useState(`Rappel de paiement — Facture ${facture.numero} (${niveauLabel})`);
  const [body, setBody] = useState(
    "Bonjour,\n\n"
    + `Malgré nos précédents envois, nous n'avons pas reçu de règlement pour votre facture ${facture.numero}. `
    + `Vous trouverez ci-joint le ${niveauLabel.toLowerCase()} correspondant.\n\n`
    + "Merci de procéder au paiement dans les meilleurs délais via le bulletin QR joint.\n\n"
    + "Cordialement,\nCar Maintenance Meyrin",
  );

  // Fetch client email au montage
  useEffect(() => {
    if (!facture.client_id) return;
    setLoadingClient(true);
    api.get(`/clients/${facture.client_id}`)
      .then(({ data }) => {
        const e = (data?.email || "").trim();
        setClientEmail(e);
        setEmailInput(e);
        const opts = [];
        const seen = new Set();
        [
          { email: e, label: "principal" },
          { email: (data?.email_factures || "").trim(), label: "facturation" },
        ].forEach((o) => {
          if (o.email && o.email.includes("@") && !seen.has(o.email.toLowerCase())) {
            seen.add(o.email.toLowerCase());
            opts.push(o);
          }
        });
        setClientEmails(opts);
        if (!e && opts.length) setEmailInput(opts[0].email);
      })
      .catch(() => setClientEmail(""))
      .finally(() => setLoadingClient(false));
  }, [facture.client_id]);

  const hasClientEmail = clientEmail && clientEmail.length > 0;
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailInput.trim());
  const canConfirm = channel === "courrier" || (channel === "email" && emailValid);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="relance-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-600" /> {label}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-gray-50 rounded p-3">
            <div className="text-xs text-gray-500">Facture</div>
            <div className="font-semibold">{facture.numero} — {facture.contact_label}</div>
            <div className="text-xs text-gray-500 mt-1">Retard : {facture.jours_retard} jour(s)</div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>Reste à payer :</div>
            <div className="text-right font-mono">{fmt(facture.reste_a_payer)} CHF</div>
            <div>Frais de rappel :</div>
            <div className="text-right font-mono">{fmt(frais)} CHF</div>
            <div className="font-semibold">Montant réclamé :</div>
            <div className="text-right font-mono font-semibold text-red-600">{fmt(total)} CHF</div>
          </div>

          {/* iter169 — Choix canal */}
          <div className="border-t pt-3 space-y-2">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Canal d&apos;envoi</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setChannel("courrier")}
                className={`p-2 rounded border text-xs font-semibold flex items-center justify-center gap-2 transition ${channel === "courrier" ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 hover:border-gray-400"}`}
                data-testid="relance-channel-courrier"
              >
                <Download className="w-3.5 h-3.5" /> Courrier (PDF)
              </button>
              <button
                type="button"
                onClick={() => setChannel("email")}
                className={`p-2 rounded border text-xs font-semibold flex items-center justify-center gap-2 transition ${channel === "email" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 hover:border-gray-400"}`}
                data-testid="relance-channel-email"
              >
                <Send className="w-3.5 h-3.5" /> Email
              </button>
            </div>

            {channel === "email" && (
              <div className="space-y-2 bg-blue-50/40 border border-blue-200 rounded p-2">
                <div className="text-[11px] text-gray-600">
                  {loadingClient
                    ? "Chargement…"
                    : hasClientEmail
                      ? <span>Email du client : <b>{clientEmail}</b></span>
                      : <span className="text-orange-700">⚠ Ce client n&apos;a pas d&apos;email — saisissez-en un ci-dessous</span>}
                </div>
                <Input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="prenom.nom@example.com"
                  data-testid="relance-email-input"
                  className={!emailValid && emailInput ? "border-red-300" : ""}
                />
                {clientEmails.length > 1 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="relance-email-options">
                    {clientEmails.map((opt, i) => {
                      const active = opt.email.toLowerCase() === emailInput.trim().toLowerCase();
                      return (
                        <button
                          key={opt.email}
                          type="button"
                          onClick={() => setEmailInput(opt.email)}
                          className={`px-2 py-0.5 rounded-full border text-[11px] ${
                            active ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                          }`}
                          data-testid={`relance-email-option-${i}`}
                        >
                          {opt.email} <span className="opacity-70">{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {emailInput && emailInput !== clientEmail && (
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={saveEmail} onChange={(e) => setSaveEmail(e.target.checked)} data-testid="relance-save-email" />
                    <span>Enregistrer cet email dans la fiche du client</span>
                  </label>
                )}

                {/* iter276 — Objet et texte relus avant l'envoi */}
                <div className="space-y-1 pt-1 border-t border-blue-200/60">
                  <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                    Objet de l&apos;email
                  </label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    data-testid="relance-email-subject"
                  />
                  <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider block pt-1">
                    Texte de l&apos;email
                  </label>
                  <Textarea
                    rows={7}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="text-xs"
                    data-testid="relance-email-body"
                  />
                  <p className="text-[10px] text-gray-500">
                    Le PDF du rappel et le bulletin QR sont joints automatiquement. Votre signature est ajoutée à la fin.
                  </p>
                </div>
              </div>
            )}
          </div>

          {facture.has_installments && !facture.installment_broken && (
            <div className={`p-3 rounded border ${shouldBreak ? "bg-orange-50 border-orange-300" : "bg-blue-50 border-blue-200"}`}>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={breakInstallments}
                  onChange={(e) => setBreakInstallments(e.target.checked)}
                  data-testid="relance-break-installments"
                  className="mt-0.5"
                />
                <div className="text-xs">
                  <div className="font-semibold">
                    {shouldBreak ? "⚠ Recommandé — Casser l'échelonnement" : "Casser l'échelonnement"}
                  </div>
                  <div className="text-gray-600 mt-0.5">
                    {shouldBreak
                      ? "Une tranche est en retard de plus de 15 jours — le total est exigible."
                      : "Force la fin du paiement par tranches et rend le solde total dû immédiatement."}
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* iter215 — Cooldown 10 jours entre 2 rappels */}
          {!rappelReady && daysUntilNext > 0 && (
            <div className="p-3 rounded border bg-amber-50 border-amber-300">
              <div className="flex items-start gap-2">
                <div className="mt-0.5">⏳</div>
                <div className="text-xs flex-1">
                  <div className="font-semibold text-amber-900">
                    Cooldown de 10 jours — {daysUntilNext} jour{daysUntilNext > 1 ? "s" : ""} à attendre
                  </div>
                  <div className="text-amber-800 mt-0.5">
                    Un rappel a déjà été envoyé récemment. Il est conseillé d&apos;attendre 10 jours entre 2 rappels
                    {facture.next_rappel_available_at && (
                      <> (prochain rappel possible le {facture.next_rappel_available_at.slice(0, 10)}).</>
                    )}
                  </div>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={forceCooldown}
                      onChange={(e) => setForceCooldown(e.target.checked)}
                      data-testid="relance-force-cooldown"
                    />
                    <span className="font-medium text-amber-900">
                      Forcer maintenant (je sais ce que je fais)
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={() => onConfirm(breakInstallments, {
              mode: channel,
              email: emailInput.trim(),
              saveEmail,
              force: forceCooldown,
              subject: subject.trim(),
              body: body.trim(),
            })}
            disabled={!canConfirm || (!rappelReady && !forceCooldown)}
            className={channel === "email" ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-orange-600 hover:bg-orange-700 text-white"}
            data-testid="relance-confirm-btn"
          >
            {channel === "email" ? "Envoyer par email" : "Générer & télécharger PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelFactureDialog({ facture, onClose, onConfirm }) {
  const [motif, setMotif] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="cancel-facture-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" /> Annuler la facture
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs">
            <b>{facture.numero}</b> — {facture.contact_label}<br />
            <span className="text-gray-600">Reste : {fmt(facture.reste_a_payer)} CHF</span>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">Motif (optionnel)</label>
            <textarea
              rows={3}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              className="w-full mt-1 border border-gray-300 rounded p-2 text-sm"
              placeholder="Erreur de facturation, doublon, geste commercial…"
              data-testid="cancel-motif"
            />
          </div>
          <p className="text-xs text-gray-500">
            <b>Attention</b> — Cette action génère les contre-écritures comptables (extourne CO 957).
            La facture reste dans l&apos;historique mais passe au statut <b>annulée</b>. Si des encaissements
            sont liés, ils devront être annulés au préalable ou vous devrez utiliser le mode <i>force</i>
            depuis la page détail.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Retour</Button>
          <Button onClick={() => onConfirm(motif)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-confirm-btn">
            Confirmer l&apos;annulation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HideRelanceDialog({ facture, onClose, onConfirm }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="hide-relance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EyeOff className="w-5 h-5 text-gray-600" /> Masquer des rappels
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs">
            <b>{facture.numero}</b> — {facture.contact_label}<br />
            <span className="text-gray-600">Reste : {fmt(facture.reste_a_payer)} CHF</span>
          </div>
          <p className="text-xs text-gray-600">
            La facture ne remontera plus dans les suggestions de rappel automatique. Elle garde son
            statut actuel (elle reste comptable et due), mais ne polluera plus votre liste de travail.
          </p>
          <p className="text-xs text-gray-500">
            Cas d&apos;usage : le client a réglé en cash mais l&apos;encaissement n&apos;est pas encore saisi,
            arrangement à part validé oralement, ou facture gérée hors système. Réversible.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={onConfirm} className="bg-gray-800 hover:bg-gray-900 text-white" data-testid="hide-confirm-btn">
            Masquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PoursuiteConfirmDialog({ facture, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="poursuite-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gavel className="w-5 h-5 text-red-600" /> Passer en poursuite
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs">
            <b>{facture.numero}</b> — {facture.contact_label}<br />
            <span className="text-gray-600">Reste : {fmt(facture.reste_a_payer)} CHF · {facture.jours_retard} jours de retard</span>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">Note (optionnel)</label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full mt-1 border border-gray-300 rounded p-2 text-sm"
              placeholder="Motif, contact office des poursuites, référence dossier…"
              data-testid="poursuite-note"
            />
          </div>
          <p className="text-xs text-gray-500">
            La facture passe au statut <b>poursuite</b>. Aucun rappel supplémentaire ne pourra être envoyé et elle sortira des suggestions.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={() => onConfirm(note)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="poursuite-confirm-btn">
            Confirmer la poursuite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

